import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { createAuthenticatedClient } from '@/lib/graphql';
import { useAuth } from '@/lib/auth/AuthContext';

// Types
export interface InviteRequest {
  id: string;
  email: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  adminNotes: string | null;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Invite {
  token: string;
  email: string;
  sentAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  isExpired: boolean;
  isRedeemed: boolean;
}

export interface ApproveInviteRequestInput {
  id: string;
  adminNotes?: string;
}

export interface DenyInviteRequestInput {
  id: string;
  adminNotes?: string;
}

// Queries
const GET_INVITE_REQUESTS = gql`
  query GetInviteRequests($status: InviteRequestStatus) {
    inviteRequests(status: $status) {
      id
      email
      status
      adminNotes
      emailVerified
      createdAt
      updatedAt
    }
  }
`;

const GET_INVITE_REQUEST = gql`
  query GetInviteRequest($id: String!) {
    inviteRequest(id: $id) {
      id
      email
      status
      adminNotes
      emailVerified
      createdAt
      updatedAt
    }
  }
`;

// Mutations
const APPROVE_INVITE_REQUEST = gql`
  mutation ApproveInviteRequest($input: ApproveInviteRequestInput!) {
    approveInviteRequest(input: $input) {
      inviteRequest {
        id
        email
        status
        adminNotes
        updatedAt
      }
      invite {
        token
        email
        sentAt
        expiresAt
      }
    }
  }
`;

const DENY_INVITE_REQUEST = gql`
  mutation DenyInviteRequest($input: DenyInviteRequestInput!) {
    denyInviteRequest(input: $input) {
      inviteRequest {
        id
        email
        status
        adminNotes
        updatedAt
      }
    }
  }
`;

// Hooks
export function useInviteRequests(status?: 'PENDING' | 'APPROVED' | 'DENIED') {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['inviteRequests', status],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ inviteRequests: InviteRequest[] }>(
        GET_INVITE_REQUESTS,
        { status }
      );
      return data.inviteRequests;
    },
    enabled: !!accessToken,
  });
}

export function useInviteRequest(id: string) {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: ['inviteRequest', id],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{ inviteRequest: InviteRequest | null }>(
        GET_INVITE_REQUEST,
        { id }
      );
      return data.inviteRequest;
    },
    enabled: !!accessToken && !!id,
  });
}

export function useApproveInviteRequest() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ApproveInviteRequestInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{
        approveInviteRequest: {
          inviteRequest: InviteRequest;
          invite: Invite;
        };
      }>(APPROVE_INVITE_REQUEST, { input });
      return data.approveInviteRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inviteRequests'] });
    },
  });
}

export function useDenyInviteRequest() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DenyInviteRequestInput) => {
      if (!accessToken) throw new Error('Not authenticated');
      const client = createAuthenticatedClient(accessToken);
      const data = await client.request<{
        denyInviteRequest: {
          inviteRequest: InviteRequest;
        };
      }>(DENY_INVITE_REQUEST, { input });
      return data.denyInviteRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inviteRequests'] });
    },
  });
}
